import React, { useCallback, useMemo, useRef, useState } from 'react';
import Build from '../../core/Build';
import { BuildCardBuildPreview } from './BuildCards';
import BuildStats from '../stats/BuildStats';
import BuildSetsPanel from './BuildSetsPanel';
import ElementBackground from '../utils/ElementBackground';
import Tooltip from '../utils/Tooltip';
import { getPublicBuild } from '../../services/buildApi';
import { loadItemCatalog } from '../../services/itemCatalog';
import './BuildTooltip.css';

const noop = () => {};

const VARIANT_FULL = 'full';
const VARIANT_STATS = 'stats';
/** Placeholder stat lines drawn while a hovered build is being fetched. */
const SKELETON_ROWS = 7;

/**
 * Fetched builds, keyed by "<owner slug>/<public code>".
 *
 * Module-level and unbounded on purpose: entries are only added by hovering, a
 * page holds at most a few dozen cards, and it is discarded on navigation. The
 * point is that re-hovering the same card never refetches.
 */
const fullBuildCache = new Map();

/**
 * A community listing ships cards without equipment — that payload is what blew
 * the Supabase egress budget (48 cards x ~110KB a page, most never hovered). So
 * a card's build arrives without `items`, and the tooltip, which is the only
 * thing that wants them, fetches them when it opens.
 *
 * Detected rather than flagged by the caller: "My builds" serves whole builds
 * from a different endpoint and must keep rendering instantly, so the test is
 * whether the equipment is actually here, not which screen we are on.
 */
function needsFullBuild(entry, buildModel) {
    if (!entry?.publicCode || !entry?.owner?.public_slug) return false;
    const items = buildModel?.items;
    return !Array.isArray(items) || !items.some(Boolean);
}

function cacheKey(entry) {
    return `${entry.owner.public_slug}/${entry.publicCode}`;
}

/** Placeholder shown while the hovered build is in flight. */
function BuildTooltipSkeleton({ variant }) {
    const statsOnly = variant === VARIANT_STATS;
    return (
        <div
            className={`build-tooltip build-tooltip--loading${statsOnly ? ' build-tooltip--stats' : ''}`}
            role="status"
            aria-live="polite"
        >
            {statsOnly ? null : <div className="build-tooltip-skeleton-gear" />}
            <div className="build-tooltip-skeleton-stats">
                {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                    <div key={i} className="build-tooltip-skeleton-row" />
                ))}
            </div>
        </div>
    );
}


const CLASS_LOGO_PREFIX = '/img/logo_transparent_';
const TXT_LVL = { fr: 'Niv.', en: 'Lv.', de: 'St.', es: 'Niv.', pt: 'Nív.' };
const DEFAULT_LVL = 200;

function getClassLogoImage(build) {
    const name = build?.getCharacterClass?.()?.name;
    return name ? `${CLASS_LOGO_PREFIX}${String(name).toLowerCase()}.png` : '';
}

function lvlLabel(lang, lvl) {
    return `${TXT_LVL[lang] || TXT_LVL.en} ${lvl}`;
}

/**
 * Hover panel: gear preview (L/R slots + trophies) over an elemental-gradient +
 * class-emblem backdrop, plus compact read-only stats.
 * Pass variant="stats" to show only the stats column (detailed cards already show gear).
 */
export function BuildTooltipBody({ build, lang, genConfig, variant = VARIANT_FULL }) {
    if (!build) return null;

    const statsOnly = variant === VARIANT_STATS;

    return (
        <div className={`build-tooltip${statsOnly ? ' build-tooltip--stats' : ''}`}>
            {statsOnly ? null : (
                <BuildTooltipGear build={build} lang={lang} />
            )}
            <div className="build-tooltip-stats">
                <BuildStats
                    build={build}
                    setBuild={noop}
                    lang={lang}
                    genConfig={genConfig || {}}
                    setGenConfig={noop}
                    canEditBuild={false}
                    canEditGenerator={false}
                />
            </div>
        </div>
    );
}

function BuildTooltipGear({ build, lang }) {
    const classLogo = getClassLogoImage(build);
    const lvl = build.getLvl?.() ?? build.lvl ?? DEFAULT_LVL;

    return (
        <div className="build-tooltip-gear">
            <ElementBackground build={build} />
            {classLogo ? (
                <img src={classLogo} className="build-tooltip-class-logo" alt="" aria-hidden="true" />
            ) : null}
            <span className="build-tooltip-lvl">{lvlLabel(lang, lvl)}</span>
            <div className="build-tooltip-gear-inner">
                <BuildCardBuildPreview buildModel={build} lang={lang} />
                <BuildSetsPanel
                    build={build}
                    setBuild={noop}
                    lang={lang}
                    canEditBuild={false}
                    variant="strip"
                    hideWhenEmpty
                />
            </div>
        </div>
    );
}

/**
 * Tooltip wrapper that shows {@link BuildTooltipBody} on pointer hover.
 * Use variant="stats" for stats-only (detailed cards); default includes gear.
 * Stats variant defaults to side (left/right); full defaults to center (above/below).
 */
export default function BuildTooltip({
    children,
    build,
    buildRaw,
    lang,
    genConfig,
    entry,
    variant = VARIANT_FULL,
    className = '',
    wrapperClassName = '',
    positionMode,
    positionOptions = { gap: 10 },
}) {
    const [fetched, setFetched] = useState(null);
    const requestedRef = useRef(false);

    const baseModel = useMemo(() => {
        if (build) return build;
        if (buildRaw) return new Build(buildRaw);
        return null;
    }, [build, buildRaw]);

    const partial = useMemo(
        () => needsFullBuild(entry, baseModel),
        [entry, baseModel],
    );

    const loadFullBuild = useCallback(() => {
        if (!partial || requestedRef.current) return;
        requestedRef.current = true;

        const cached = fullBuildCache.get(cacheKey(entry));
        if (cached) {
            setFetched(cached);
            return;
        }
        // The catalog is fetched alongside the build, not before it: a saved
        // build is item *ids*, so constructing one without the catalog yields
        // sixteen empty slots and a stat sheet missing every item and set
        // bonus. Nothing on the community page warms the catalog the way the
        // generator does, so this is the only place that can.
        //
        // Both requests go out together rather than in sequence — the catalog
        // is usually already in memory, and when it is not there is no reason
        // to make the build wait behind it.
        Promise.all([
            getPublicBuild(entry.owner.public_slug, entry.publicCode),
            loadItemCatalog(),
        ])
            .then(([resolved]) => {
                const payload = resolved?.build;
                if (!payload?.build) return;
                const record = { build: new Build(payload.build), genConfig: payload.genConfig };
                fullBuildCache.set(cacheKey(entry), record);
                setFetched(record);
            })
            .catch(() => {
                // Let the next hover retry: a card that silently never fills in
                // is worse than one that tries again.
                requestedRef.current = false;
            });
    }, [entry, partial]);

    const model = fetched?.build || baseModel;
    const effectiveGenConfig = fetched?.genConfig ?? genConfig;
    const statsOnly = variant === VARIANT_STATS;

    // While a partial build is still in flight the panel must render something,
    // or Tooltip would treat empty content as "nothing to show" and never open.
    let content = null;
    if (partial && !fetched) {
        content = <BuildTooltipSkeleton variant={variant} />;
    } else if (model) {
        content = (
            <BuildTooltipBody
                build={model}
                lang={lang}
                genConfig={effectiveGenConfig}
                variant={variant}
            />
        );
    }

    const shellMod = statsOnly ? ' build-tooltip-shell--stats' : '';
    const mode = positionMode ?? (statsOnly ? 'side' : 'center');

    return (
        <Tooltip
            content={content}
            className={`build-tooltip-shell${shellMod}${className ? ` ${className}` : ''}`}
            wrapperClassName={`build-tooltip-wrap${wrapperClassName ? ` ${wrapperClassName}` : ''}`}
            positionMode={mode}
            positionOptions={positionOptions}
            onShow={loadFullBuild}
        >
            {children}
        </Tooltip>
    );
}
